import { Box, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import { derivePhoneMessage, type PhoneScreen } from "./phoneMessage";
import { PhonePair } from "./PhonePair";
import { PreviewColumn, PreviewSections, PreviewStack } from "./preview.styles";
import { RecipientView } from "./RecipientView";
import type { TextComposerPreviewContent } from "./useMessagePreview";
import {
  getDerivedOutput,
  type TextComposerDerivedOutput,
} from "../channel/channelDerivation";
import { AuthoringStatePreview } from "../compose/layout.styles";
import type { TextComposerAuthoringState } from "../editor/authoringState";
import { buildTextComposerContributedVariantConfig } from "../frame/buildFrameUpdatePayload";
import {
  resolveFrameVariantForChannel,
  type TextComposerFramePreviewOutput,
} from "../frame/frameOutput";
import type { PreviewFillParams } from "@/api/dragon-tiles/previewUserContext";
import type { RecognitionUser } from "@/api/profiles";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";

import { useAccountSite } from "@/api/siteDetails";
import { hasEditorAccess } from "@/utils/editorAccess";

interface PreviewPanelProps {
  siteId: string;
  authoringState: TextComposerAuthoringState;
  frameOutput: TextComposerFramePreviewOutput;
  previewByChannel: {
    RCS: TextComposerPreviewContent | null;
    SMS: TextComposerPreviewContent | null;
    MMS: TextComposerPreviewContent | null;
  };
  previewRecipientViewMode: "primary" | "fallback";
  onPreviewRecipientViewModeChange: (nextMode: "primary" | "fallback") => void;
  selectedProductModel?: ProductGridModelValue;
  isPreviewError?: boolean;
  onPreviewRetry?: () => void;
  channelPair?: TextComposerDerivedOutput;
  selectedProfile?: RecognitionUser | null;
  onSelectedProfileChange?: (profile: RecognitionUser | null) => void;
  previewFillParams?: PreviewFillParams;
}

export function PreviewPanel({
  siteId,
  authoringState,
  frameOutput,
  previewByChannel,
  previewRecipientViewMode,
  onPreviewRecipientViewModeChange,
  selectedProductModel,
  isPreviewError,
  onPreviewRetry,
  channelPair,
  selectedProfile,
  onSelectedProfileChange,
  previewFillParams,
}: PreviewPanelProps) {
  const { data: site } = useAccountSite(siteId);
  const showFrameObject = hasEditorAccess();
  const [screen, setScreen] = useState<PhoneScreen>("app");
  const derivedOutput = useMemo(
    () => channelPair ?? getDerivedOutput(authoringState),
    [channelPair, authoringState],
  );
  const frameVariantConfig = useMemo(
    () =>
      buildTextComposerContributedVariantConfig({
        authoringState,
        productModel: selectedProductModel,
      }),
    [authoringState, selectedProductModel],
  );
  const frameOutputJson = useMemo(() => {
    if (!showFrameObject) return "";
    const enriched = {
      frame: {
        frame_variants: frameOutput.frame.frame_variants.map((v) => ({
          ...v,
          config: frameVariantConfig,
        })),
      },
    };
    return JSON.stringify(enriched, null, 2);
  }, [frameOutput, showFrameObject, frameVariantConfig]);
  const mediaNameByUrl = useMemo(
    () =>
      Object.fromEntries(
        (authoringState.media ?? []).map((item) => [item.cdnUrl, item.name]),
      ),
    [authoringState.media],
  );
  const hasFallback = derivedOutput.fallbackChannel !== null;
  const activeChannel =
    previewRecipientViewMode === "fallback" && hasFallback
      ? derivedOutput.fallbackChannel
      : derivedOutput.primaryChannel;

  const message = useMemo(
    () =>
      derivePhoneMessage({
        activeChannel,
        previewByChannel,
        mediaNameByUrl,
        brandName: site?.brand_name,
        authoringState,
        isPreviewError,
      }),
    [
      activeChannel,
      previewByChannel,
      mediaNameByUrl,
      site?.brand_name,
      authoringState,
      isPreviewError,
    ],
  );

  const testSendVariant = useMemo(
    () => resolveFrameVariantForChannel(frameOutput, activeChannel),
    [frameOutput, activeChannel],
  );

  return (
    <PreviewColumn>
      <RecipientView
        siteId={siteId}
        testSendVariant={testSendVariant}
        config={frameVariantConfig}
        primaryChannel={derivedOutput.primaryChannel}
        fallbackChannel={derivedOutput.fallbackChannel}
        viewMode={previewRecipientViewMode}
        onViewModeChange={onPreviewRecipientViewModeChange}
        selectedProfile={selectedProfile}
        onSelectedProfileChange={onSelectedProfileChange}
        previewFillParams={previewFillParams}
        screen={screen}
        onScreenChange={setScreen}
      />

      <PreviewSections>
        <PreviewStack role="region" aria-label="Message preview">
          <PhonePair
            message={message}
            screen={screen}
            onRetry={onPreviewRetry}
          />
        </PreviewStack>

        {showFrameObject ? (
          <Box component="section" role="region" aria-label="Frame object">
            <Typography variant="subtitle2" color="text.contrastText">
              Frame object
            </Typography>
            <AuthoringStatePreview>{frameOutputJson}</AuthoringStatePreview>
          </Box>
        ) : null}
      </PreviewSections>
    </PreviewColumn>
  );
}
